/**
 * UX-B1 (#295) — toast กลางจุดเดียวสำหรับ mutation ที่ fail โดยหน้านั้นไม่มี handler เฉพาะ
 * QueryClient กลาง (lib/query-client.ts) เรียก notifyMutationError จาก MutationCache.onError
 * แสดง message + request reference (request_id ถ้ามี) — ปิดเองได้ / หายเองใน 8 วินาที
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { errorMessage } from "./ErrorBanner";

export type MutationErrorToast = { id: number; error: unknown };

type Listener = (toast: MutationErrorToast) => void;
let listeners: Listener[] = [];
let nextId = 1;

/** QueryClientMutationCache.onError → ประกาศ error ให้ toaster (ไม่มี subscriber = ทิ้งไป) */
export function notifyMutationError(error: unknown): void {
  if (listeners.length === 0) return;
  for (const l of listeners) l({ id: nextId++, error });
}

export function MutationErrorToaster() {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<MutationErrorToast[]>([]);

  useEffect(() => {
    const listener: Listener = (toast) => {
      setToasts((prev) => [...prev.slice(-2), toast]);
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== toast.id));
      }, 8_000);
    };
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((l) => l !== listener);
    };
  }, []);

  if (toasts.length === 0) return null;
  return (
    <div className="fixed right-4 bottom-4 z-[60] flex w-[min(24rem,92vw)] flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          className="flex items-start gap-2 rounded-md border border-danger bg-danger-bg px-3 py-2 text-sm text-danger shadow-lg"
        >
          <span className="flex-1 break-all">
            <span className="font-semibold">{t("common.mutation_failed")}: </span>
            {errorMessage(toast.error, t("common.error"))}
          </span>
          <button
            type="button"
            aria-label={t("common.close")}
            className="btn-icon"
            onClick={() => setToasts((prev) => prev.filter((x) => x.id !== toast.id))}
          >
            <X size={14} aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
}
